// apps/web/src/widgets/ranked-tier/tier-css.ts
//
// Phase 7 — recipe-data-to-CSS-custom-property bridge (UI-SPEC §"Ranked-Tier
// Visual Contract" line 451 + RESEARCH §Pattern 3). PURE helper — testable
// without DOM. The output object is passed straight to React's `style` attribute
// on the renderer root. The resulting CSS custom properties are consumed by
// the renderer's rank-keyed static classes — the SOLE permitted inline style
// in Phase 7 per UI-SPEC line 805.
//
// Branch B (per Task 0 probe 2026-06-09): Tailwind v4.3.0's lib.mjs implements
// the `@source inline(...)` directive (verified via parser source —
// `_.startsWith("inline(")` handler) but the inline-pattern parser performs
// only brace expansion (`{a,b,c}` via `Kt`/`St` helpers), NOT glob `*`.
// Because tier IDs are author-declared (kebab-case per /^[a-z][a-z0-9-]*$/)
// and cannot be enumerated at build time, Branch A would require either
// listing every possible id (unbounded) or `*` (invalid). The plan's planned
// Branch B fallback applies: 36 named static classes keyed by rank declared
// in apps/web/src/styles/globals.css; this helper emits RANK-based variable
// keys (`--tier-rank-{N}-{bg|border|fg}`). D-06 caps N <= 12 so the universe
// is bounded by construction.
//
// Sanitization: tier colors are recipe content; Pass 2A schema validation (Plan
// 03 RankedTierConfigSchema) enforces non-empty length-bounded color strings.
// This helper does NOT re-validate. Helmet CSP `style-src 'self' 'unsafe-inline'`
// (Phase 6 contingency) covers the inline style attribute. The browser CSS
// parser rejects non-color tokens at apply time (threat-model T-07-09-02).
//
// PITFALLS §N3 structural mitigation: this file pairs with the renderer's
// `data-tier-rank` + `data-tier-id` attributes — NO named legacy tier classes
// (the four-tier closed-enum names) anywhere. The Plan 01 grep gate
// (scripts/grep-no-recipe-identifiers.sh extended to apps/web/src/**) enforces
// the ban; rank-based names (`tier-rank-{N}-*`) are NOT in the banned list —
// they're data-driven, recipe-N-tier-friendly hooks. // grep-allow: tier-good, tier-ok, tier-bad
//
// isolatedDeclarations + verbatimModuleSyntax + exactOptionalPropertyTypes
// (Phase 01.1 D-10/D-15): the CSSProperties type from React is closed and does
// NOT admit `[propName: string]` custom-property keys. The documented React
// escape hatch is a `Record<string, string>` accumulator with an `as
// CSSProperties` cast at return — exactly what this helper does.
import type { RankedTier } from "@the-recipe-spread/shared";
import type { CSSProperties } from "react";

export function buildTierCssVars(tiers: ReadonlyArray<RankedTier>): CSSProperties {
  // Working accumulator typed as Record<string, string>; the CSSProperties type
  // signature doesn't admit custom-property keys cleanly. The `as CSSProperties`
  // cast at return is the one acknowledged escape hatch (RESEARCH §Pattern 3).
  // Branch B: keys are RANK-based, NOT id-based — they pair with the 36 named
  // static classes in globals.css.
  const acc: Record<string, string> = {};
  for (const t of tiers) {
    acc[`--tier-rank-${t.rank}-bg`] = t.colors.bg;
    acc[`--tier-rank-${t.rank}-border`] = t.colors.border;
    acc[`--tier-rank-${t.rank}-fg`] = t.colors.fg;
  }
  return acc as CSSProperties;
}

export function sortTiersByRank(tiers: ReadonlyArray<RankedTier>): ReadonlyArray<RankedTier> {
  // Native sort, stable in V8 (RESEARCH §"Don't Hand-Roll" line 640). The
  // spread copy is required because Array.prototype.sort mutates in place;
  // sorting a ReadonlyArray directly is both a TS error and a renderer-side
  // correctness bug (would mutate config.tiers across re-renders).
  return [...tiers].sort((a, b) => a.rank - b.rank);
}
